import {
  FileUpload as FileUploadIcon,
  Close as CloseIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from '@mui/icons-material';
import { Box, Button, FormHelperText, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { getBeautufiedFileSize, getMegabytesFromBytes } from '@/core';
import './FileUploader.css';

type FileToUpload = {
  file: File;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error: string;
};

type FileUploaderProps = {
  onFileUpload?: (file: FileToUpload) => Promise<any>;
  onUploadFinished?: (allFilesSuccessfullyUploaded: boolean, files: FileToUpload[]) => void;
  maxFileSize?: number;
};

const removeDuplicateFiles = (
  previousFiles: FileToUpload[],
  newFiles: FileToUpload[]
): FileToUpload[] => {
  const previousFilesNames = previousFiles.map((e) => e.file.name);
  return newFiles.filter((e) => !previousFilesNames.includes(e.file.name));
};

const FileUploader = ({ onFileUpload, onUploadFinished, maxFileSize = 25 }: FileUploaderProps) => {
  const [error, setError] = useState('');
  const [files, setFiles] = useState<FileToUpload[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const currentlyUploadingFileIndex = useMemo(() => {
    return files.findIndex((e) => e.status === 'uploading');
  }, [files]);

  const isFirstTimeUpload = useMemo(() => {
    return files.every((e) => e.status === 'idle');
  }, [files]);

  const areInteractionsDisabled = currentlyUploadingFileIndex !== -1;

  useEffect(() => {
    if (currentlyUploadingFileIndex === -1) {
      return;
    }

    (async () => {
      const updatedFiles = [...files];

      try {
        if (onFileUpload) {
          await onFileUpload(updatedFiles[currentlyUploadingFileIndex]);
        }

        updatedFiles[currentlyUploadingFileIndex].status = 'success';
      } catch (uploadError: unknown) {
        updatedFiles[currentlyUploadingFileIndex].status = 'error';

        const e = uploadError as Error;

        if (e instanceof ApiError) {
          updatedFiles[currentlyUploadingFileIndex].error = getErrorMessage(
            e.errorResponseDto.message
          );
        } else {
          updatedFiles[currentlyUploadingFileIndex].error = t(
            'An error occurred while uploading file.',
            { defaultValue: 'An error occurred while uploading file.' }
          );
        }
      }

      if (updatedFiles.length > currentlyUploadingFileIndex + 1) {
        updatedFiles[currentlyUploadingFileIndex + 1].status = 'uploading';
        setFiles(updatedFiles);
        return;
      }

      setFiles(updatedFiles);

      const uploadFileStatuses = updatedFiles.map((e) => e.status);
      const hasAnyFileFailedToUpload = uploadFileStatuses.includes('error');

      if (hasAnyFileFailedToUpload) {
        setError(
          t('Some files could not be uploaded. Please try again.', {
            defaultValue: 'Some files could not be uploaded. Please try again.',
          }).toString()
        );
      }

      if (onUploadFinished) {
        onUploadFinished(!hasAnyFileFailedToUpload, updatedFiles);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentlyUploadingFileIndex, onFileUpload, enqueueSnackbar]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (areInteractionsDisabled) {
      return;
    }

    if (!e.dataTransfer.files) {
      return;
    }

    const uniqueDroppedFiles = removeDuplicateFiles(
      files,
      Array.from(e.dataTransfer.files).map((file) => ({ file, status: 'idle', error: '' }))
    );

    if (uniqueDroppedFiles.length === 0) {
      return;
    }

    const updatedFiles = [...files, ...uniqueDroppedFiles];

    setFiles(updatedFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleSelectFilesClick = () => {
    if (areInteractionsDisabled) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (areInteractionsDisabled) {
      return;
    }

    if (!e.target.files) {
      return;
    }

    const uniqueDroppedFiles = removeDuplicateFiles(
      files,
      Array.from(e.target.files).map((file) => ({ file, status: 'idle', error: '' }))
    );

    if (uniqueDroppedFiles.length === 0) {
      return;
    }

    const updatedFiles = [...files, ...uniqueDroppedFiles];

    setFiles(updatedFiles);
  };

  const handleUploadClick = () => {
    setError('');

    if (files.length <= 0) {
      setError(
        t('Please select files to upload.', {
          defaultValue: 'Please select files to upload.',
        }).toString()
      );
      return;
    }

    // Filter out files that are larger than maximum size
    const updatedFiles = files.filter(
      (file) => getMegabytesFromBytes(file.file.size) <= maxFileSize
    );

    if (updatedFiles.length <= 0) {
      setError(
        t('Your files do not meet size requirements.', {
          defaultValue: 'Your files do not meet size requirements.',
        }).toString()
      );
      return;
    }

    updatedFiles[0].status = 'uploading';

    setFiles(updatedFiles);
  };

  const handleRetryUploadClick = () => {
    setError('');

    if (files.length <= 0) {
      setError(
        t('Please select files to upload.', {
          defaultValue: 'Please select files to upload.',
        }).toString()
      );
      return;
    }

    // Filter out files that are larger than maximum size
    let updatedFiles = files.filter((file) => getMegabytesFromBytes(file.file.size) <= maxFileSize);

    if (updatedFiles.length <= 0) {
      setError(
        t('Your files do not meet size requirements.', {
          defaultValue: 'Your files do not meet size requirements.',
        }).toString()
      );
    }

    // Filter out files that were successfully uploaded
    updatedFiles = files.filter((file) => file.status !== 'success');

    if (updatedFiles.length <= 0) {
      setError(
        t('There are no files left.', {
          defaultValue: 'There are no files left.',
        }).toString()
      );
    }
    if (updatedFiles[0]) {
      updatedFiles[0].status = 'uploading';

      setFiles(updatedFiles);
    }
  };

  return (
    <Box>
      <Box
        sx={(theme) => ({
          border: '2px dashed gray',
          borderRadius: `${theme.shape.borderRadius}px`,
          padding: '1rem',
          cursor: areInteractionsDisabled ? 'not-allowed' : 'pointer',
          mt: '0.2rem',
        })}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleSelectFilesClick}
      >
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <FileUploadIcon sx={{ fontSize: '3rem' }} />
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ color: 'grey.600' }}>
              {t('Drop items here or', { defaultValue: 'Drop items here or' })}
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              &nbsp;{t('Browse files', { defaultValue: 'Browse files' })}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography sx={{ color: 'grey.600', fontSize: '0.75rem', mt: '0.2rem' }}>
        {t('Up to {{maxFileSize}} MB', { defaultValue: 'Up to {{maxFileSize}} MB', maxFileSize })}{' '}
        &#9679;{' '}
        {t('File names without special characters', {
          defaultValue: 'File names without special characters',
        })}
      </Typography>
      <Grid container gap="0.6rem" sx={{ mt: '1rem' }}>
        {!!files &&
          files.map((file, index) => {
            const isFileTooLarge = getMegabytesFromBytes(file.file.size) > maxFileSize;

            return (
              <Grid
                item
                xs={12}
                key={file.file.name}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.grey[600]}`,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  opacity: file.status === 'uploading' ? '0.5' : '1.0',
                  position: 'relative',
                })}
              >
                {file.status === 'uploading' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      height: '2px',
                      zIndex: 1,
                      backgroundColor: 'primary.main',
                      top: 0,
                    }}
                    className="loading-animation"
                  />
                )}
                <Box sx={{ display: 'flex' }}>
                  <Box
                    sx={(theme) => ({
                      backgroundColor: theme.palette.grey[200],
                      display: 'flex',
                      alignItems: 'center',
                      p: '1rem',
                      borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                      borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                      position: 'relative',
                    })}
                  >
                    {(isFileTooLarge || file.status === 'error' || file.status === 'success') && (
                      <Box
                        sx={(theme) => ({
                          position: 'absolute',
                          height: '100%',
                          backgroundColor:
                            file.status === 'success' ? 'success.light' : 'error.light',
                          width: '0.3rem',
                          left: 0,
                          top: 0,
                          borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                          borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                        })}
                      />
                    )}
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: '2rem' }} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      ml: '1rem',
                    }}
                  >
                    <Typography>{file.file.name}</Typography>
                    {isFileTooLarge || file.status === 'error' || file.status === 'success' ? (
                      <Typography
                        sx={(theme) => ({
                          fontSize: '0.8rem',
                          color:
                            file.status === 'success'
                              ? theme.palette.success.light
                              : theme.palette.error.light,
                        })}
                      >
                        <>
                          {isFileTooLarge &&
                            t('File size is too large (max. {{maxFileSize}}MB)', {
                              defaultValue: 'File size is too large (max. {{maxFileSize}}MB)',
                              maxFileSize,
                            })}
                          {file.status === 'error' && file.error}
                          {file.status === 'success' &&
                            t('Successfully uploaded file.', {
                              defaultValue: 'Successfully uploaded file.',
                            })}
                        </>
                      </Typography>
                    ) : (
                      <Typography
                        sx={(theme) => ({
                          fontSize: '0.8rem',
                          color: theme.palette.grey[600],
                        })}
                      >
                        {getBeautufiedFileSize(file.file.size)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: '1rem',
                  }}
                >
                  <CloseIcon
                    onClick={() => {
                      if (areInteractionsDisabled) {
                        return;
                      }

                      const newFiles = [...files];
                      newFiles.splice(index, 1);

                      setFiles(newFiles);
                    }}
                    sx={{
                      fontSize: '2rem',
                      cursor: areInteractionsDisabled ? 'not-allowed' : 'pointer',
                    }}
                  />
                </Box>
              </Grid>
            );
          })}
      </Grid>
      {isFirstTimeUpload && (
        <Button
          variant="contained"
          fullWidth
          onClick={handleUploadClick}
          sx={{ mt: '1rem' }}
          disabled={areInteractionsDisabled}
        >
          {areInteractionsDisabled
            ? t('Uploading...', { defaultValue: 'Uploading...' })
            : t('Upload', { defaultValue: 'Upload' })}
        </Button>
      )}
      {!isFirstTimeUpload && (
        <Button
          variant="contained"
          fullWidth
          onClick={handleRetryUploadClick}
          sx={{ mt: '1rem' }}
          disabled={areInteractionsDisabled}
        >
          {areInteractionsDisabled
            ? t('Uploading...', { defaultValue: 'Uploading...' })
            : t('Retry upload', { defaultValue: 'Retry upload' })}
        </Button>
      )}
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default FileUploader;
